import { Dialog, DialogTitle, DialogContent, Button, Box } from "@mui/material";
import { Document, Page } from "react-pdf";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EstimateDto } from "../../contracts/estimates/types";
import { usePopup } from "../../hooks/usePopup.ts";

interface PdfPreviewDialogProps {
  estimateDto: EstimateDto;
  open: boolean;
  blob: Blob | null;
  onClose: () => void;
  fileName?: string;
  downloadPath?: string; // e.g. "/popup-download/estimate" or "/popup-download/return-request"
}

export default function PdfPreviewDialog({
  estimateDto, open, blob, onClose, fileName = 'preview.pdf', downloadPath = '/popup-download/estimate'
}: PdfPreviewDialogProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const { openDownloadPopup } = usePopup();

  const measure = useCallback(() => {
    if (!wrapRef.current) return;
    const w = wrapRef.current.getBoundingClientRect().width; 
    setContainerWidth(Math.max(0, Math.floor(w)));
  }, []);

  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const startObserve = useCallback(() => {
    if (!wrapRef.current) return;

    roRef.current?.disconnect();

    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => {
        measure(); 
        const ro = new ResizeObserver(measure);
        ro.observe(wrapRef.current!);
        roRef.current = ro;
      });
      (startObserve as any)._r2 = r2;
    });
    (startObserve as any)._r1 = r1;
  }, [measure]);

  useEffect(() => {
    if (!open) {
      roRef.current?.disconnect();
      roRef.current = null;
      return;
    }
    return () => {
      const r1 = (startObserve as any)._r1;
      const r2 = (startObserve as any)._r2;
      if (r1) cancelAnimationFrame(r1);
      if (r2) cancelAnimationFrame(r2);
      roRef.current?.disconnect();
      roRef.current = null;
    };
  }, [open, startObserve]);

  const onEntered = () => {
    startObserve();
  };

  const onDownloadPopup = () => {
      openDownloadPopup({
          path: downloadPath,
          key: `${estimateDto.id}`,
          payloadData: estimateDto,
          windowName: "견적서 파일 다운로드"
      });
  };

  const pageWidth = Math.floor((containerWidth || 0) * scale);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      TransitionProps={{ onEntered }}
    >
      <DialogTitle sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        PDF 미리보기
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Button onClick={() => setScale(s => Math.max(0.5, +(s - 0.1).toFixed(2)))}>-</Button>
          <Button onClick={() => setScale(1)}>100%</Button>
          <Button onClick={() => setScale(s => Math.min(2, +(s + 0.1).toFixed(2)))}>+</Button>
          <Button onClick={onDownloadPopup}>다운로드</Button>
          <Button onClick={onClose}>닫기</Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          ref={wrapRef}
          sx={{
            width: "100%", height: "85vh", overflow: "auto",
            minWidth: 0, boxSizing: "border-box",
            px: 0, py: 2, bgcolor: "background.default",
          }}
        >
          {blob ? (
            <Document
              file={blob}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={<Box sx={{ p: 3 }}>로딩 중…</Box>}
              error={<Box sx={{ p: 3, color: "error.main" }}>PDF 로딩 실패</Box>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex", justifyContent: "center", mb: 2,
                    width: pageWidth || "100%",
                    "& canvas": { width: "100% !important", height: "auto !important" },
                  }}
                >
                  <Page
                    pageNumber={i + 1}
                    width={pageWidth || undefined} 
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Box>
              ))}
            </Document>
          ) : (
            <Box sx={{ p: 3, color: "text.secondary" }}>미리보기가 없습니다.</Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
